import type { ReactNode } from "react";

import { EmptyState, formatProviderFullName } from "@stacklet/ui";
import { graphql, useLazyLoadQuery } from "react-relay";
import { generatePath, Navigate } from "react-router";

import { paths } from "app/constants/paths";
import { useProvider } from "app/contexts/ProviderContext";
import { useEnabledDashboardsFilterElement } from "app/hooks/feature-flags/useEnabledDashboards/useEnabledDashboardsFilterElement";

import type { DashboardProviderGuardQuery } from "./__generated__/DashboardProviderGuardQuery.graphql";

interface Props {
  /** The dashboard key from the current route (`:key`). */
  dashboardKey: string;
  /** Rendered when the route already matches the selected provider. */
  children: ReactNode;
}

/**
 * Redirects to the selected provider's cost dashboard when the route `:key`
 * belongs to a different provider, so the page matches the sidebar selection.
 */
export function DashboardProviderGuard({ dashboardKey, children }: Props) {
  const provider = useProvider();
  const filterElement = useEnabledDashboardsFilterElement();

  const data = useLazyLoadQuery<DashboardProviderGuardQuery>(
    graphql`
      query DashboardProviderGuardQuery($filterElement: FilterElementInput) {
        dashboards(filterElement: $filterElement) {
          edges {
            node {
              key
            }
          }
        }
      }
    `,
    { filterElement },
    { fetchPolicy: "store-or-network" },
  );

  const keys = (data.dashboards.edges ?? []).map((edge) => edge.node.key);

  if (keys.includes(dashboardKey)) {
    return children;
  }

  // `key.includes("cost")` mirrors how DashboardContent classifies cost dashboards.
  const target = keys.find((key) => key.includes("cost"));
  if (target) {
    return (
      <Navigate to={generatePath(paths.dashboard, { key: target })} replace />
    );
  }

  // This provider has no cost dashboard (e.g. Tencent Cloud in some
  // environments) — explain the switch rather than silently redirecting away.
  return (
    <EmptyState
      message={`There is no cost dashboard for ${formatProviderFullName(
        provider,
      )}. Select a different provider or report from the sidebar.`}
      title="No cost dashboard"
    />
  );
}
